import axios from 'axios';
import { Button, Modal, Textarea, useSnackbar } from '@hue/design-system';
import { useContext, useState, type ReactNode } from 'react';
import { getApiError, getApiErrorCode } from '../lib/api.js';
import { ClosedPeriodContext } from './closed-period-context.js';

/// Quando uma gravação mexe num mês já fechado na Contabilidade gerencial, o servidor recusa
/// (ADR 0064, decisão 5). A tela oferece os dois caminhos: lançar a diferença como ajuste de mês
/// anterior, com motivo, ou reabrir o mês no Fechamento. Mora na casca porque o Financeiro e a
/// Contabilidade gerencial usam o mesmo diálogo, e um módulo não importa o outro.

export interface ClosedPeriodOverride {
  reason: string;
}

const MONTH_NAMES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const monthName = (month: string) =>
  `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} de ${month.slice(0, 4)}`;

function closedMonthOf(error: unknown): string | null {
  if (getApiErrorCode(error) !== 'ACCOUNTING_PERIOD_CLOSED') return null;
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const month = (body as { details?: { month?: unknown } } | undefined)?.details?.month;
  return typeof month === 'string' ? month : null;
}

interface Pending {
  month: string;
  retry: (override: ClosedPeriodOverride) => Promise<unknown>;
  after: () => Promise<unknown> | void;
}

export interface ClosedPeriodHandler {
  /// Diz se a recusa era de mês fechado e, sendo, abre o diálogo. `retry` grava de novo com o
  /// pedido de ajuste; `after` é o que a tela faz quando a gravação passa.
  intercept(
    error: unknown,
    retry: (override: ClosedPeriodOverride) => Promise<unknown>,
    after: () => Promise<unknown> | void,
  ): boolean;
  dialog: ReactNode;
}

export function useClosedPeriod(): ClosedPeriodHandler {
  const scope = useContext(ClosedPeriodContext);
  const snackbar = useSnackbar();
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const canAdjust = scope?.canAdjust ?? false;
  const canReopen = scope?.canReopen ?? false;
  const today = scope?.today ?? '';

  const close = () => {
    if (saving) return;
    setPending(null);
    setReason('');
    setError(null);
  };

  async function adjust() {
    if (!pending) return;
    if (reason.trim().length < 3) {
      setError('Escreva o motivo do ajuste.');
      return;
    }
    setSaving(true);
    try {
      await pending.retry({ reason: reason.trim() });
      await pending.after();
      snackbar.success(
        `Salvo. A diferença de ${monthName(pending.month)} entra como ajuste em ${monthName(today.slice(0, 7))}.`,
      );
      setPending(null);
      setReason('');
      setError(null);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setSaving(false);
    }
  }

  const dialog =
    pending && scope ? (
      <Modal
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
        title={`${monthName(pending.month).replace(/^./u, (letter) => letter.toUpperCase())} está fechado`}
        description={`Esta mudança mexe em ${monthName(pending.month)}, que já foi fechado na Contabilidade gerencial. Nada foi salvo ainda. Escolha como corrigir.`}
        footer={
          <>
            <Button variant="ghost" disabled={saving} onClick={close}>
              Cancelar
            </Button>
            {canAdjust ? (
              <Button disabled={saving} onClick={() => void adjust()}>
                {saving ? 'Salvando…' : 'Salvar e lançar ajuste'}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="space-y-4 text-sm">
          <div className="space-y-2 rounded-xl border border-surface-border p-3">
            <p className="font-medium">Lançar como ajuste de mês anterior</p>
            <p className="text-content-muted">
              Para quando os números de {monthName(pending.month)} já foram enviados aos sócios ou
              ao contador. O mês continua como foi fechado; a diferença entra em{' '}
              {monthName(today.slice(0, 7))}, na linha "Ajuste de {monthName(pending.month)}",
              abaixo do resultado e fora dele.
            </p>
            {canAdjust ? (
              <Textarea
                label="Motivo do ajuste"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Ex.: conta lançada no lugar errado, descoberta na revisão."
                error={error ?? undefined}
              />
            ) : (
              <p className="text-content-muted">
                Seu perfil não pode lançar ajuste. Peça a quem tem a permissão "Lançar ajuste de mês
                anterior".
              </p>
            )}
          </div>
          <div className="space-y-2 rounded-xl border border-surface-border p-3">
            <p className="font-medium">Reabrir {monthName(pending.month)}</p>
            <p className="text-content-muted">
              Para quando os números ainda não saíram da empresa. Reabrir também reabre os meses
              seguintes que estiverem fechados; depois de corrigir, feche de novo.
            </p>
            {canReopen ? (
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => {
                  close();
                  scope.openClosing();
                }}
              >
                Ir para o Fechamento
              </Button>
            ) : (
              <p className="text-content-muted">
                Seu perfil não pode reabrir mês. Peça a quem tem a permissão "Reabrir mês contábil
                fechado".
              </p>
            )}
          </div>
        </div>
      </Modal>
    ) : null;

  return {
    intercept(failure, retry, after) {
      const month = closedMonthOf(failure);
      if (!month || !scope) return false;
      setPending({ month, retry, after });
      setReason('');
      setError(null);
      return true;
    },
    dialog,
  };
}
